/**
 * KpiCard — card sofisticado com:
 * - Animação count-up
 * - Sparkline embutido
 * - Trend % com seta + cor
 * - Gradient sutil + glow no hover
 * - Click feedback
 * - Skeleton loading
 */
import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

import { Sparkline } from "@/components/sparkline";
import { useCountUp } from "@/lib/use-count-up";
import { cn } from "@/lib/utils";

type Color = "primary" | "success" | "warning" | "danger" | "purple" | "info";

const PALETTES: Record<Color, {
  gradient: string;
  text: string;
  badge: string;
  glow: string;
  sparkColor: "primary" | "success" | "warning" | "danger" | "purple";
}> = {
  primary: {
    gradient: "from-sky-500/10 via-sky-500/[0.03] to-transparent",
    text: "text-sky-600 dark:text-sky-300",
    badge: "bg-sky-100 dark:bg-sky-500/15",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(14,165,233,0.18),0_18px_50px_-18px_rgba(14,165,233,0.35)]",
    sparkColor: "primary",
  },
  success: {
    gradient: "from-emerald-500/10 via-emerald-500/[0.03] to-transparent",
    text: "text-emerald-600 dark:text-emerald-300",
    badge: "bg-emerald-100 dark:bg-emerald-500/15",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(16,185,129,0.18),0_18px_50px_-18px_rgba(16,185,129,0.35)]",
    sparkColor: "success",
  },
  warning: {
    gradient: "from-amber-500/10 via-amber-500/[0.03] to-transparent",
    text: "text-amber-600 dark:text-amber-300",
    badge: "bg-amber-100 dark:bg-amber-500/15",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(245,158,11,0.18),0_18px_50px_-18px_rgba(245,158,11,0.35)]",
    sparkColor: "warning",
  },
  danger: {
    gradient: "from-rose-500/10 via-rose-500/[0.03] to-transparent",
    text: "text-rose-600 dark:text-rose-300",
    badge: "bg-rose-100 dark:bg-rose-500/15",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(244,63,94,0.18),0_18px_50px_-18px_rgba(244,63,94,0.35)]",
    sparkColor: "danger",
  },
  purple: {
    gradient: "from-violet-500/10 via-violet-500/[0.03] to-transparent",
    text: "text-violet-600 dark:text-violet-300",
    badge: "bg-violet-100 dark:bg-violet-500/15",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(139,92,246,0.18),0_18px_50px_-18px_rgba(139,92,246,0.35)]",
    sparkColor: "purple",
  },
  info: {
    gradient: "from-cyan-500/10 via-cyan-500/[0.03] to-transparent",
    text: "text-cyan-600 dark:text-cyan-300",
    badge: "bg-cyan-100 dark:bg-cyan-500/15",
    glow: "group-hover:shadow-[0_0_0_1px_rgba(6,182,212,0.18),0_18px_50px_-18px_rgba(6,182,212,0.35)]",
    sparkColor: "primary",
  },
};

interface KpiCardProps {
  label: string;
  /** Valor numérico pra animar; passe null se ainda loading */
  value: number | null;
  /** Display formatado pronto (substitui count-up); use pra strings tipo "R$ 12.345,67" */
  display?: string;
  /** Prefix do count-up (ex: "R$ ") */
  prefix?: string;
  /** Suffix do count-up (ex: "%") */
  suffix?: string;
  /** Casas decimais no count-up */
  decimals?: number;
  hint?: string;
  /** Trend % vs período anterior */
  trend?: number | null;
  /** Sparkline pra preview de série */
  sparkData?: number[];
  icon?: LucideIcon;
  color?: Color;
  /** Click handler — vira card clicável */
  onClick?: () => void;
  /** Subvalue (e.g. comparação total) */
  subValue?: string;
  loading?: boolean;
  /** Animação stagger (em ms) */
  delay?: number;
}

export function KpiCard({
  label,
  value,
  display,
  prefix,
  suffix,
  decimals,
  hint,
  trend,
  sparkData,
  icon: Icon,
  color = "primary",
  onClick,
  subValue,
  loading,
  delay = 0,
}: KpiCardProps) {
  const palette = PALETTES[color];
  const countUp = useCountUp(value, {
    duration: 1100,
    decimals,
    prefix,
    suffix,
    enabled: display === undefined,
  });

  const finalDisplay = display ?? (value === null ? "—" : countUp.display);

  const trendUp = (trend ?? 0) > 0;
  const trendDown = (trend ?? 0) < 0;

  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      type={onClick ? "button" : undefined}
      onClick={onClick}
      style={{ animationDelay: `${delay}ms` }}
      className={cn(
        "group relative overflow-hidden rounded-2xl",
        "border border-slate-200/60 dark:border-white/10",
        "bg-white/70 dark:bg-card/40 backdrop-blur-xl",
        "p-5 text-left",
        "transition-all duration-300 ease-out",
        "animate-in fade-in-0 slide-in-from-bottom-3 duration-700 fill-mode-both",
        onClick && [
          "cursor-pointer",
          "hover:-translate-y-1",
          "hover:bg-white/85 dark:hover:bg-card/55",
          palette.glow,
        ],
      )}
    >
      {/* Gradient background sutil */}
      <div
        aria-hidden
        className={cn(
          "absolute inset-0 -z-10 bg-gradient-to-br transition-opacity duration-500",
          palette.gradient,
          "opacity-60 group-hover:opacity-100",
        )}
      />
      {/* Glow blob no canto */}
      <div
        aria-hidden
        className={cn(
          "absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-40 transition-opacity duration-500",
          palette.badge,
          "group-hover:opacity-80",
        )}
      />

      <div className="relative">
        <div className="flex items-start justify-between gap-2">
          <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            {label}
          </p>
          {Icon && (
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-lg shrink-0 transition-transform duration-300",
                palette.badge, palette.text,
                "group-hover:scale-110 group-hover:rotate-3",
              )}
            >
              <Icon className="h-4 w-4" strokeWidth={2.5} />
            </div>
          )}
        </div>

        <div className="mt-3 flex items-end justify-between gap-3 min-h-[44px]">
          <div className="min-w-0 flex-1">
            {loading ? (
              <div className="h-9 w-24 rounded-lg bg-slate-200/70 dark:bg-white/10 animate-pulse" />
            ) : (
              <p
                className={cn(
                  "font-bold text-3xl sm:text-[34px] tracking-tight tabular-nums leading-none truncate",
                  "transition-all duration-300",
                  countUp.isAnimating && "text-foreground",
                )}
                title={finalDisplay}
              >
                {finalDisplay}
              </p>
            )}
            {subValue && (
              <p className="mt-1 text-[11px] text-muted-foreground tabular-nums truncate">
                {subValue}
              </p>
            )}
          </div>

          {sparkData && sparkData.length > 0 && (
            <Sparkline
              data={sparkData}
              color={palette.sparkColor}
              width={72}
              height={36}
              filled
              className="shrink-0 opacity-80 group-hover:opacity-100 transition-opacity"
            />
          )}
        </div>

        {/* Trend + hint */}
        <div className="mt-2.5 flex items-center justify-between gap-2">
          {typeof trend === "number" ? (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-md",
                trendUp && "text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/10",
                trendDown && "text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/10",
                !trendUp && !trendDown && "text-muted-foreground bg-slate-50 dark:bg-white/5",
              )}
            >
              {trendUp ? <ArrowUpRight className="h-3 w-3" /> : trendDown ? <ArrowDownRight className="h-3 w-3" /> : null}
              {Math.abs(trend).toFixed(1)}%
            </span>
          ) : <span />}
          {hint && (
            <p className="text-[10px] text-muted-foreground truncate">{hint}</p>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
